import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import SubscriptionTabs from "../../../components/AdminCentral/Subscriptions/SubscriptionTabs";
import SubscriptionsContent from "../../../components/AdminCentral/Subscriptions/SubscriptionsContents";
import ReportDownloadLogContent from "../../../components/AdminCentral/Subscriptions/ReportDownloadLogContent";
import ImportInvoicesContent from "../../../components/AdminCentral/Subscriptions/ImportInvoicesContent";
import POCUpdatesContent from "../../../components/AdminCentral/Subscriptions/POCUpdates";
import ExpiringSubscriptionsContent from "../../../components/AdminCentral/Subscriptions/ExpiringSubscriptionContent";
import SubscriptionAnnouncements from "../../../components/AdminCentral/Subscriptions/SubscriptionAnnouncements";
import POCContent from "../../../components/AdminCentral/Subscriptions/POCContent";
import RenewSubscriptionContent from "../../../components/AdminCentral/Subscriptions/RenewSubscriptionsContent";
import usePersistentTabState from "../../../hooks/usePersistentTabState";

const breakpoints = {
  tablet: 768,
  phone:  480,
};

const ISAFE_ROLES = new Set([
  "isafe_accountant",
  "isafe_moderator",
  "isafe_salesperson",
  "isafe_admin",
  "root",
]);
const SUBSCRIPTION_ROLES = new Set([
  "subscription_access",
  "subscription_accountant",
  "subscription_admin",
  "subscription_implementor",
  "subscription_salesperson",
]);

const ADMIN_SUBSCRIPTION_TABS = [
  "Subscriptions",
  "Report Download Log",
  "Import Invoices",
  "POC Updates",
  "Expiring Subscriptions",
];

const CUSTOMER_SUBSCRIPTION_TABS = [
  "Announcements",
  "Point of Contact",
  "Renew Subscription",
];

const DashboardContainer = styled.div`
  width: 1180px;
  min-width: 100%;
  min-height: 100vh;
  padding: 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 20px;
  overflow-y: auto;
  scrollbar-gutter: stable;

  @media (max-width: ${breakpoints.tablet}px) {
    padding: 20px;
    gap: 20px;
  }

  @media (max-width: ${breakpoints.phone}px) {
    // padding: 16px 12px;
    gap: 16px;
  }
`;

const Inner = styled.div`
  width: 100%;
  max-width: 1320px;
  margin: 0 auto;
  background-color: ${({ colors }) => colors.cardBackground};
  border-radius: 6px;
  padding: 24px 28px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-self: stretch;

  @media (max-width: ${breakpoints.tablet}px) {
    width: 400px;
    padding: 20px;
    gap: 20px;
  }

  @media (max-width: ${breakpoints.phone}px) {
    padding: 16px;
    gap: 16px;
  }
`;

const ScrollableSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: stretch;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: ${breakpoints.phone}px) {
    gap: 16px;
  }
`;

const ContentPanel = styled.div`
  width: 100%;
  flex: 1 0 100%;
  display: flex;
  flex-direction: column;
  min-width: 0;

  & > * {
    width: 100%;
  }
`;

const Title = styled.h1`
  font-size: 36px;
  color: ${({ colors }) => colors.titleText};
  font-weight: 600;
  margin: 0;

  @media (max-width: ${breakpoints.tablet}px) {
    font-size: 32px;
  }

  @media (max-width: ${breakpoints.phone}px) {
    font-size: 1.6rem;
  }
`;

export default function SubscriptionsPage({ colors }) {
  const location = useLocation();
  const navigate = useNavigate();

  const become = useSelector((s) => s.auth.become);
  const roles = useSelector((s) => s.auth.user.roles || []);
  const hasISAFERole = roles.some((r) => ISAFE_ROLES.has(r.id));
  const hasSubscriptionAdminRole = roles.some((r) => SUBSCRIPTION_ROLES.has(r.id));

  const requestedTab = location.state?.defaultTab;
  const preferredDefault =
    become === 0 && hasISAFERole ? "Subscriptions" : "Announcements";
  const [selectedTab, setSelectedTab] = usePersistentTabState(
    "admincentral.subscriptions.selectedTab",
    requestedTab || preferredDefault
  );

  useEffect(() => {
    if (requestedTab) {
      setSelectedTab(requestedTab);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [requestedTab, navigate, location.pathname, setSelectedTab]);

  const handleTabClick = (tab) => setSelectedTab(tab);
  const showISAFETabs = become === 0 && hasISAFERole;
  const showSubscriptionTabs = become === 1 || hasSubscriptionAdminRole;

  const availableTabs = useMemo(() => {
    if (showISAFETabs) return ADMIN_SUBSCRIPTION_TABS;
    if (showSubscriptionTabs) return CUSTOMER_SUBSCRIPTION_TABS;
    return [];
  }, [showISAFETabs, showSubscriptionTabs]);

  useEffect(() => {
    if (!availableTabs.length) return;
    if (!selectedTab || !availableTabs.includes(selectedTab)) {
      const fallback =
        (requestedTab && availableTabs.includes(requestedTab))
          ? requestedTab
          : availableTabs[0];
      setSelectedTab(fallback);
    }
  }, [availableTabs, selectedTab, requestedTab, setSelectedTab]);

  return (
    <DashboardContainer>
      <Title colors={colors}>Subscriptions</Title>
      <Inner colors={colors}>

        {/* Tabs can overflow horizontally on small */}
        <ScrollableSection>
          <SubscriptionTabs
            colors={colors}
            selectedTab={selectedTab}
            handleTabClick={handleTabClick}
          />
        </ScrollableSection>

        {/* Each content area can also scroll internally if needed */}
        <ScrollableSection>
          {showISAFETabs && (
            <>
              {selectedTab === "Subscriptions" && (
                <ContentPanel>
                  <SubscriptionsContent colors={colors} />
                </ContentPanel>
              )}
              {selectedTab === "Report Download Log" && (
                <ContentPanel>
                  <ReportDownloadLogContent colors={colors} />
                </ContentPanel>
              )}
              {selectedTab === "Import Invoices" && (
                <ContentPanel>
                  <ImportInvoicesContent colors={colors} />
                </ContentPanel>
              )}
              {selectedTab === "POC Updates" && (
                <ContentPanel>
                  <POCUpdatesContent colors={colors} />
                </ContentPanel>
              )}
              {selectedTab === "Expiring Subscriptions" && (
                <ContentPanel>
                  <ExpiringSubscriptionsContent colors={colors} />
                </ContentPanel>
              )}
            </>
          )}

          {showSubscriptionTabs && (
            <>
              {selectedTab === "Announcements" && (
                <ContentPanel>
                  <SubscriptionAnnouncements colors={colors} />
                </ContentPanel>
              )}
              {selectedTab === "Point of Contact" && (
                <ContentPanel>
                  <POCContent colors={colors} />
                </ContentPanel>
              )}
              {selectedTab === "Renew Subscription" && (
                <ContentPanel>
                  <RenewSubscriptionContent colors={colors} />
                </ContentPanel>
              )}
            </>
          )}
        </ScrollableSection>
      </Inner>
    </DashboardContainer>
  );
}
